import React, {FC, useEffect, useState} from 'react'
import {Modal, Button, Table, Spinner} from 'react-bootstrap'
import moment from 'moment'
import useAxiosPrivate from '../../../../libs/useAxiosPrivate'
import {getRecordActivityLogs} from '../core/_requests'
import {ActivityLog} from '../core/_models'

interface ModalAuditLogProps {
  show: boolean
  onHide: () => void
  tableName: string
  recordId?: number | string
}

// Raw DB column names → human-readable labels, reusing the label strings
// already used for these fields in AgreementDetailView.tsx / AgreementDetailNewView.tsx.
// Unmapped keys fall back to the raw key name.
const FIELD_LABELS: Record<string, string> = {
  Agreement: 'Name',
  agreement: 'Name',
  AgreementType: 'Type',
  type: 'Type',
  acronym: 'Short Name',
  wto_rta_id: 'WTO RTA Id',
  status: 'Status',
  notification: 'Notification',
  date_signed: 'Date Signed',
  date_into_force: 'Date Into Force',
  date_notification: 'Date Notification',
  end_implementation: 'End Implementation',
  date_inactive: 'Date Inactive',
  Composition: 'Composition',
  composition: 'Composition',
  subregion: 'Subregion',
  parties_wto: 'Parties WTO',
  crossregional: 'Crossregional',
  language: 'Language',
  sources: 'Sources',
  parties_escap: 'Parties ESCAP',
  services_schedules: 'Service Schedule',
  desta_id: 'Desta Id',
  iso: 'Parties',
  // Chapter/content-table fields (dss_trade_agreement_detail_formatted[_temp],
  // dss_trade_agreement_detail_long[_temp]) — surfaced once the per-agreement
  // history was expanded to include chapter add/edit/reorder/delete actions.
  chapter_number: 'Chapter Number',
  chapter_name: 'Chapter Name',
  article_number: 'Article Number',
  article_name: 'Article Name',
  article_text: 'Article Text',
  article_text_display: 'Article Text (Display)',
  clean_content: 'Content',
  display_content: 'Content (Display)',
  chapter_id: 'Chapter Id',
  article_id: 'Article Id',
  parent_id: 'Parent',
  sort_order: 'Sort Order',
  nwords_new: 'Word Count',
  source_file_path: 'Source File',
  is_source_valid: 'Source Valid',
  filename: 'Filename',
  label: 'Label',
}

type DiffRow = {
  key: string
  label: string
  oldValue: string
  newValue: string
}

const formatDiffValue = (value: any): string => {
  if (value === null || value === undefined || value === '') return '-'
  if (Array.isArray(value)) return value.join(', ') || '-'
  return String(value)
}

const safeParse = (raw?: string | null): Record<string, any> => {
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

const buildDiff = (prevRaw?: string | null, newRaw?: string | null): DiffRow[] => {
  const prev = safeParse(prevRaw)
  const next = safeParse(newRaw)

  const keys = Array.from(new Set([...Object.keys(prev), ...Object.keys(next)]))

  return keys
    .filter((key) => formatDiffValue(prev[key]) !== formatDiffValue(next[key]))
    .map((key) => ({
      key,
      label: FIELD_LABELS[key] || key,
      oldValue: formatDiffValue(prev[key]),
      newValue: formatDiffValue(next[key]),
    }))
}

const ModalAuditLog: FC<ModalAuditLogProps> = ({show, onHide, tableName, recordId}) => {
  const axiosPrivate = useAxiosPrivate()
  const [logs, setLogs] = useState<ActivityLog[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [expandedId, setExpandedId] = useState<number | null>(null)

  useEffect(() => {
    if (!show || !recordId) return

    setIsLoading(true)
    setExpandedId(null)

    getRecordActivityLogs(axiosPrivate, tableName, recordId)
      .then((res) => {
        setLogs(res?.data?.data || [])
      })
      .catch((err) => {
        console.error(err)
        setLogs([])
      })
      .finally(() => {
        setIsLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, tableName, recordId])

  const toggleExpand = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  return (
    <Modal show={show} onHide={onHide} centered size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>Audit Log</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{maxHeight: '65vh', overflowY: 'auto'}}>
        {isLoading ? (
          <div
            className='d-flex align-items-center justify-content-center'
            style={{height: '150px'}}
          >
            <Spinner animation='border' role='status' variant='primary' />
          </div>
        ) : logs.length === 0 ? (
          <div className='text-center text-muted py-5'>No activity logs found for this record.</div>
        ) : (
          <Table striped bordered hover responsive size='sm'>
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Description</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const diff = buildDiff(log.prev_value, log.new_value)
                const isExpanded = expandedId === log.id

                return (
                  <React.Fragment key={log.id}>
                    <tr>
                      <td className='text-nowrap'>
                        {moment(log.created_at).format('DD MMM YYYY, HH:mm:ss')}
                      </td>
                      <td>{log.creator_name || '-'}</td>
                      <td>{log.description}</td>
                      <td className='text-end'>
                        {diff.length > 0 && (
                          <Button variant='link' size='sm' onClick={() => toggleExpand(log.id)}>
                            {isExpanded ? 'Hide changes' : 'Show changes'}
                          </Button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && diff.length > 0 && (
                      <tr>
                        <td colSpan={4} className='py-3 px-4'>
                          <ul className='mb-0 ps-3'>
                            {diff.map((d) => (
                              <li key={d.key}>
                                <strong>{d.label}:</strong> {d.oldValue} &rarr; {d.newValue}
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </Table>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant='default' onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ModalAuditLog
